<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VIcon } from 'vuetify/components';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

type Props = {
  icon: string | IconDefinition;
  size?: number | string;
  color?: string;
  [key: string]: any; // Pass through any other props
};
const props = withDefaults(defineProps<Props>(), {
  size: 24,
});

const attrs = useAttrs();
const isFaIconDefinition = (icon: Props['icon']) => typeof icon === 'object' && 'icon' in icon;

const isMdi = computed(() => !isFaIconDefinition(props.icon) && props.icon.startsWith('mdi-'));
const isFa = computed(() => isFaIconDefinition(props.icon) || props.icon.includes('fa-'));

// Define the size of the icon (I know this is strange, but this is how it is in the codebase)
type FontAwesomeIconSize =
  | '2xs'
  | 'xs'
  | 'sm'
  | 'lg'
  | 'xl'
  | '2xl'
  | '1x'
  | '2x'
  | '3x'
  | '4x'
  | '5x'
  | '6x'
  | '7x'
  | '8x'
  | '9x'
  | '10x';

// Pixel sizes don't match fontawesome default size. its one above to match mdi
const fontAwesomeSizeMap: Record<number, FontAwesomeIconSize> = {
  10: '2xs', // 0.625em
  12: 'xs', // 0.75em
  14: 'sm', // 0.875em
  16: '1x', // 1em (default FontAwesome base size)
  20: 'lg', // 1.25em
  24: 'xl', // 1.5em — best match for MDI's default size
  32: '2xl', // 2em
  48: '3x',
  64: '4x',
  80: '5x',
  96: '6x',
  112: '7x',
  128: '8x',
  144: '9x',
  160: '10x',
};

const vuetifyToFontAwesomeSizeMap: Record<string, FontAwesomeIconSize> = {
  'x-small': '2xs', // ≈10px
  small: 'xs', // ≈12px
  default: 'xl', // Vuetify default is 24px, FA "xl"
  large: '2xl', // ≈32px
  'x-large': '3x', // ≈48px
};

const getFontAwesomeSize = (size?: number | string): FontAwesomeIconSize => {
  if (size == null) return 'xl'; // fallback for undefined

  // If it's a known Vuetify size name
  if (typeof size === 'string' && vuetifyToFontAwesomeSizeMap[size]) {
    return vuetifyToFontAwesomeSizeMap[size];
  }

  // Try to parse string size to number
  const parsed = typeof size === 'string' ? parseInt(size, 10) : size;

  // If it’s a valid number and exists in our FA map
  if (!isNaN(parsed) && fontAwesomeSizeMap[parsed]) {
    return fontAwesomeSizeMap[parsed];
  }

  return 'lg'; // fallback FA default (16px / 1x)
};

const iconSize = computed(() => {
  if (isMdi.value) {
    return props.size;
  }
  if (isFa.value) {
    return getFontAwesomeSize(props.size);
  }
  return undefined;
});

const internalClass = computed(() => {
  return [props.color ? `text-${props.color}` : '', (attrs.class as string) ?? ''].filter(Boolean).join(' ');
});
</script>

<template>
  <component
    :is="isMdi ? VIcon : isFa ? FontAwesomeIcon : 'span'"
    v-bind="{
      ...$props,
      icon: undefined,
      size: undefined,
    }"
    :class="internalClass"
    :style="($attrs.style, [isFa ? 'padding: 2px 0' : ''])"
    :icon="icon"
    :size="iconSize"
  >
    <template v-if="!isMdi && !isFa">
      {{ icon }}
    </template>
  </component>
</template>
<style scoped lang="scss"></style>
